/**
 * Membership auto-dedupe (cart page + drawer).
 *
 * Removes the membership product from the cart when it doesn't belong there:
 *   - the customer is ALREADY a member (they shouldn't re-buy membership), OR
 *   - a book club product is in the cart (book club bundles membership benefits).
 *
 * Background: this logic used to live in the Plus-only in-checkout extension
 * (`membership-validations`). In-checkout UI extensions require Shopify Plus, so
 * it is re-homed to the cart page/drawer to survive the Advanced downgrade.
 * Sign-in is required before checkout, so a logged-in customer's tags are
 * authoritative here. Member status is read from `window.Allstora.isMember`
 * (rendered from the customer's tags in theme.liquid).
 *
 * Mechanics (per review against this Dawn-based theme):
 *   - Match membership lines by PRODUCT id (monthly / yearly / digital), not
 *     variant, so all variants + selling plans are caught.
 *   - Remove by line KEY via /cart/change.js (keys are stable; indexes shift).
 *   - Request cart sections in the change call, then publish the theme's
 *     `cartUpdate` event so BOTH the cart page (main-cart-items) and the drawer
 *     (cart-drawer-items) re-fetch/repaint — sectionsToRender() alone only
 *     covers the drawer.
 *   - Manually patch `cart-icon-bubble` from the change.js response, since the
 *     stock onCartUpdate() handlers don't refresh the bubble.
 *   - Publish `cartUpdate` and let the theme's own onCartUpdate() re-fetch/swap
 *     the drawer items and the /cart page. We do NOT call renderContents()
 *     ourselves — a second manual repaint races the theme's re-fetch.
 *   - Toggle the `is-empty` class on the cart wrappers ourselves when the last
 *     line is removed. The theme's onCartUpdate() only swaps inner item HTML; it
 *     never sets `is-empty` (only updateQuantity() does), so without this the
 *     emptied drawer shows a blank panel with a stale count instead of the
 *     "Your cart is empty" view.
 *   - Re-entry guard (`isDeduping`) + a source guard on the published event
 *     prevent loops.
 */

(function () {
  const MEMBERSHIP_PRODUCT_IDS = [
    window.Allstora?.membership?.product_id,
    window.Allstora?.membership_yearly?.product_id,
    window.Allstora?.digital_membership_yearly?.product_id,
  ]
    .filter((id) => id != null)
    .map((id) => Number(id));

  const SOURCE = 'membership-auto-dedupe';

  // Prevents the removal -> cartUpdate -> re-run loop.
  let isDeduping = false;

  // Re-render the drawer synchronously from the 'cart-drawer' section HTML that
  // change.js already returned (no async fetch → no flash), then toggle is-empty
  // and publish so the /cart page + icon bubble update via the theme's own
  // subscribers.
  function renderCartViaTheme(state) {
    const isEmpty = state.item_count === 0;
    const drawer = document.querySelector('cart-drawer');
    const mainCartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');

    // Swap the drawer's inner markup synchronously from the section HTML the
    // change.js call already returned — no async fetch, so no flash. The
    // 'cart-drawer' section's #CartDrawer holds the full inner (items OR the
    // empty-cart block), so replacing its innerHTML gives the correct state at
    // once. Then toggle `is-empty` so the CSS shows the right variant.
    const sectionHTML = state?.sections?.['cart-drawer'];
    if (drawer && sectionHTML) {
      const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const freshInner = parsed.querySelector('#CartDrawer');
      const liveInner = drawer.querySelector('#CartDrawer');
      if (freshInner && liveInner) liveInner.innerHTML = freshInner.innerHTML;
    }

    if (drawer) drawer.classList.toggle('is-empty', isEmpty);
    if (mainCartItems) mainCartItems.classList.toggle('is-empty', isEmpty);
    if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);

    // Refresh the /cart page + icon bubble via publish (drawer already handled).
    // Our source guard prevents re-entry.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: state });
    }
  }

  // Inject the notice styles once (self-contained so it doesn't depend on a
  // repo/admin-managed stylesheet).
  function ensureNoticeStyles() {
    if (document.getElementById('membership-dedupe-notice-styles')) return;
    const style = document.createElement('style');
    style.id = 'membership-dedupe-notice-styles';
    style.textContent = `
      .membership-dedupe-notice {
        display: flex; align-items: flex-start; gap: 0.75rem;
        font-size: 1.3rem; line-height: 1.4;
        background: #f4efe6; color: #1a1a1a;
        border: 1px solid #d8ccb4; border-radius: 8px;
        padding: 1rem 1.2rem;
      }
      .membership-dedupe-notice--toast {
        position: fixed; z-index: 1000; right: 1.5rem; bottom: 1.5rem;
        max-width: 32rem; box-shadow: 0 4px 16px rgba(0,0,0,0.15);
      }
      .membership-dedupe-notice__text { flex: 1 1 auto; }
      .membership-dedupe-notice__close {
        flex: 0 0 auto; background: none; border: 0; cursor: pointer;
        font-size: 1.8rem; line-height: 1; color: inherit; padding: 0;
      }
    `;
    document.head.appendChild(style);
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function sectionsToRequest() {
    const drawer = getCartDrawer();
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((s) => s.id);
    }
    // Fallback so the icon bubble can still be patched on the /cart page.
    return ['cart-icon-bubble'];
  }

  // Manually refresh the cart-icon-bubble; stock onCartUpdate() doesn't touch it.
  function patchIconBubble(parsedState) {
    const html = parsedState?.sections?.['cart-icon-bubble'];
    if (!html) return;
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const inner = parsed.querySelector('.shopify-section');
    if (inner) bubble.innerHTML = inner.innerHTML;
  }


  // Tell the member why their membership line disappeared, so a silent removal
  // (esp. right after adding it from a product page) doesn't read as a bug.
  // Distinct copy for the member vs. book-club case. Injected into the cart /
  // drawer inner, with a fixed-toast fallback if neither container is present.
  function showDedupeNotice(reason) {
    ensureNoticeStyles();
    const message =
      reason === 'member'
        ? "You're already a member — your 30% member discount applies automatically."
        : 'Your book club already includes membership, so the separate membership was removed.';

    // Avoid duplicate notices stacking up.
    const existing = document.querySelector('.membership-dedupe-notice');
    if (existing) existing.remove();

    const notice = document.createElement('div');
    notice.className = 'membership-dedupe-notice';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.innerHTML =
      '<span class="membership-dedupe-notice__text"></span>' +
      '<button type="button" class="membership-dedupe-notice__close" aria-label="Dismiss">&times;</button>';
    notice.querySelector('.membership-dedupe-notice__text').textContent = message;
    notice
      .querySelector('.membership-dedupe-notice__close')
      .addEventListener('click', () => notice.remove());

    // Anchor to <body> as a fixed toast, NOT inside the cart/drawer sections.
    // Our own publish(cartUpdate) triggers the theme's onCartUpdate(), which
    // async-fetches fresh section HTML and replaces the section innerHTML AFTER
    // this runs — an inline notice inside those sections would flash and vanish.
    // A body-anchored toast survives every section swap.
    notice.classList.add('membership-dedupe-notice--toast');
    document.body.appendChild(notice);

    // Auto-dismiss after a few seconds; the close button remains for a11y.
    setTimeout(() => notice.remove(), 8000);
  }

  window.MembershipAutoDedupe = async function MembershipAutoDedupe() {
    if (isDeduping) return;
    if (MEMBERSHIP_PRODUCT_IDS.length === 0) return;

    let cart;
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      cart = await response.json();
    } catch (error) {
      console.error('[Membership auto-dedupe] failed to read cart:', error);
      return;
    }
    window.Allstora.cart = cart;

    const isMember = window.Allstora?.isMember === true;
    const bookclubInCart = cart.items.some((item) => item.product_type === 'Bookclub');

    // Only remove membership when it doesn't belong: member already, or a book
    // club (which bundles membership) is present. `isMember` takes priority for
    // the notice copy since it's the more common case.
    if (!isMember && !bookclubInCart) return;
    const reason = isMember ? 'member' : 'bookclub';

    // Remove by line KEY, not index. updateQuantity(index) is position-based and
    // the /cart.js order can differ from the rendered DOM order right after an
    // add, which can remove the WRONG line (e.g. wipe the book instead of the
    // membership). The line key is unambiguous. Collect the membership keys:
    const membershipKeys = cart.items
      .filter((item) => MEMBERSHIP_PRODUCT_IDS.includes(Number(item.product_id)))
      .map((item) => item.key);
    if (membershipKeys.length === 0) return;

    isDeduping = true;
    try {
      // Remove each membership line by key via change.js (precise). Request the
      //'cart-drawer' section in the SAME call so the fresh drawer markup — items
      // OR the empty-cart block — comes back synchronously, with no async second
      // fetch that would flash the intermediate state.
      let lastState;
      for (const key of membershipKeys) {
        const changeResponse = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0, sections: ['cart-drawer'] }),
        });
        if (!changeResponse.ok) {
          console.error('[Membership auto-dedupe] change.js failed:', changeResponse.status);
          continue;
        }
        lastState = await changeResponse.json();
      }

      if (lastState) renderCartViaTheme(lastState);

      // Explain the removal so it doesn't read as an item silently vanishing.
      showDedupeNotice(reason);
    } catch (error) {
      console.error('[Membership auto-dedupe] removal error:', error);
    } finally {
      // Release the guard after the render settles (renderCartFromState may
      // publish cartUpdate, which our subscriber re-enters on).
      setTimeout(() => {
        isDeduping = false;
      }, 300);
    }
  };

  function run() {
    window.MembershipAutoDedupe();
  }

  // Run on initial load. With `defer` this fires before DOMContentLoaded, but
  // guard against late/async loading too.
  if (document.readyState !== 'loading') {
    run();
  } else {
    window.addEventListener('DOMContentLoaded', run);
  }

  // Re-run after any cart mutation (add-to-cart from a product page, quantity
  // changes, drawer AJAX). The theme uses its own pub/sub, not DOM events. Skip
  // our own published event to avoid immediate re-entry; `isDeduping` is a
  // second guard. Other subscribers (cart-items) only re-fetch HTML, they don't
  // re-publish cartUpdate, so there is no loop.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === SOURCE) return;
      window.MembershipAutoDedupe();
    });
  }
})();
